import { motion, useScroll, useTransform } from "framer-motion";
import { Search, Globe2, Crown, Shield, ArrowRight, CheckCircle2 } from "lucide-react";
import { Button } from "@/components/ui/button";
import { useRef } from "react";

const services = [
  {
    id: 1,
    icon: Search,
    title: "Bespoke Property Sourcing",
    tagline: "Precision meets insight",
    description: "We uncover exceptional properties tailored to your unique vision — from hidden gems to iconic estates — with meticulous attention to every detail.",
    features: [
      "Off-market opportunities",
      "Personalized property matching",
      "Detailed market analysis",
      "Virtual preview tours",
    ],
  },
  {
    id: 2,
    icon: Globe2,
    title: "Cross-Border Investment Strategy",
    tagline: "Bridging markets worldwide",
    description: "Leveraging our global network across Dubai, Monaco, Spain, and Italy, we navigate complex international acquisitions with seamless expertise.",
    features: [
      "Multi-jurisdiction expertise",
      "Tax optimization guidance",
      "Currency & timing strategy",
      "Legal partner coordination",
    ],
  },
  {
    id: 3,
    icon: Crown,
    title: "White-Glove Concierge",
    tagline: "Every detail, perfected",
    description: "From first viewing to key handover and beyond — thorough attention to every aspect of your acquisition journey, ensuring a frictionless experience.",
    features: [
      "Dedicated personal advisor",
      "End-to-end transaction support",
      "Relocation assistance",
      "Post-purchase management",
    ],
  },
  {
    id: 4,
    icon: Shield,
    title: "Fiduciary Excellence",
    tagline: "Europe's most trusted partnership",
    description: "As fiduciaries, we are legally and ethically bound to act solely in your best interest. This is the gold standard of trust — the most rigorous advisory relationship on the continent.",
    features: [
      "Legally bound to your interest",
      "Complete fee transparency",
      "Conflict-free representation",
      "Certified fiduciary standards",
    ],
    featured: true,
  },
];

const containerVariants = {
  hidden: { opacity: 0 },
  visible: {
    opacity: 1,
    transition: {
      staggerChildren: 0.15,
      delayChildren: 0.1,
    },
  },
};

const itemVariants = {
  hidden: { 
    opacity: 0, 
    y: 40,
    scale: 0.95,
  },
  visible: {
    opacity: 1,
    y: 0,
    scale: 1,
    transition: {
      type: "spring" as const,
      stiffness: 100,
      damping: 15,
    },
  },
};

const ServicesSection = () => {
  const sectionRef = useRef<HTMLDivElement>(null);
  const { scrollYProgress } = useScroll({
    target: sectionRef,
    offset: ["start end", "end start"],
  });

  // Parallax effect for decorative background
  const bgY = useTransform(scrollYProgress, [0, 1], [100, -100]);
  const bgY2 = useTransform(scrollYProgress, [0, 1], [-50, 50]);
  const bgOpacity = useTransform(scrollYProgress, [0, 0.3, 0.7, 1], [0, 0.5, 0.5, 0]);

  return (
    <section 
      ref={sectionRef}
      id="services" 
      className="relative py-24 lg:py-32 bg-background overflow-hidden"
    >
      {/* Parallax Background Elements */}
      <motion.div 
        className="absolute inset-0 pointer-events-none"
        style={{ opacity: bgOpacity }}
      >
        <motion.div 
          className="absolute top-20 -left-40 w-96 h-96 rounded-full bg-gradient-to-br from-primary/10 to-transparent blur-3xl"
          style={{ y: bgY }}
        />
        <motion.div 
          className="absolute bottom-20 -right-40 w-96 h-96 rounded-full bg-gradient-to-tl from-gold/10 to-transparent blur-3xl"
          style={{ y: bgY2 }}
        />
      </motion.div>

      <div className="container mx-auto px-6 lg:px-12 relative z-10">
        {/* Header with enhanced animation */}
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8 }}
          viewport={{ once: true, margin: "-100px" }}
          className="text-center mb-16"
        >
          <motion.p 
            className="font-body text-sm uppercase tracking-[0.3em] text-primary mb-4"
            initial={{ opacity: 0, letterSpacing: "0.1em" }}
            whileInView={{ opacity: 1, letterSpacing: "0.3em" }}
            transition={{ duration: 1, delay: 0.2 }}
            viewport={{ once: true }}
          >
            Refined Through Generations
          </motion.p>
          <h2 className="font-display text-4xl md:text-5xl lg:text-6xl font-light text-foreground mb-6 leading-tight">
            The Counsel of
            <span className="block italic font-medium">Quiet Expertise</span>
          </h2>
          <p className="font-body text-lg text-muted-foreground max-w-2xl mx-auto">
            Nearly three decades of family expertise. Thoughtful access to rare opportunities. 
            Meticulous guidance that protects and grows generational wealth.
          </p>
        </motion.div>

        {/* Services Grid with stagger animation */}
        <motion.div 
          className="grid md:grid-cols-2 gap-8 mb-12"
          variants={containerVariants}
          initial="hidden"
          whileInView="visible"
          viewport={{ once: true, margin: "-50px" }}
        >
          {services.map((service) => (
            <motion.div
              key={service.id}
              variants={itemVariants}
              className="group"
            >
              <motion.div
                whileHover={{ 
                  y: -8, 
                  scale: 1.02,
                }}
                transition={{ duration: 0.3 }}
                className={`relative h-full rounded-2xl p-8 lg:p-10 transition-all duration-500 ${
                  service.featured
                    ? "bg-gradient-to-br from-primary/10 via-primary/5 to-transparent border-2 border-primary/30 hover:border-primary/50 hover:shadow-gold"
                    : "bg-card border border-border/50 hover:border-primary/30 hover:shadow-elegant"
                }`}
              >
                {/* Featured Badge */}
                {service.featured && (
                  <motion.div 
                    className="absolute -top-3 left-8"
                    initial={{ opacity: 0, y: -10 }}
                    whileInView={{ opacity: 1, y: 0 }}
                    transition={{ delay: 0.5, duration: 0.4 }}
                    viewport={{ once: true }}
                  >
                    <span className="bg-primary text-primary-foreground text-xs font-medium uppercase tracking-wider px-4 py-1.5 rounded-full shadow-lg">
                      Most Trusted
                    </span>
                  </motion.div>
                )}

                {/* Icon with hover animation */}
                <motion.div
                  className={`w-14 h-14 rounded-xl flex items-center justify-center mb-6 transition-all duration-300 ${
                    service.featured
                      ? "bg-primary text-primary-foreground"
                      : "bg-primary/10 text-primary group-hover:bg-primary group-hover:text-primary-foreground"
                  }`}
                  whileHover={{ rotate: [0, -5, 5, 0], scale: 1.1 }}
                  transition={{ duration: 0.4 }}
                >
                  <service.icon className="w-7 h-7" strokeWidth={1.5} />
                </motion.div>

                {/* Content */}
                <div className="mb-6">
                  <p className="text-sm text-primary font-medium uppercase tracking-wider mb-2">
                    {service.tagline}
                  </p>
                  <h3 className="font-display text-2xl lg:text-3xl text-foreground mb-4">
                    {service.title}
                  </h3>
                  <p className="text-muted-foreground leading-relaxed">
                    {service.description}
                  </p>
                </div>

                {/* Features */}
                <ul className="space-y-3 mb-8">
                  {service.features.map((feature, idx) => (
                    <motion.li 
                      key={feature} 
                      className="flex items-center gap-3 text-sm"
                      initial={{ opacity: 0, x: -10 }}
                      whileInView={{ opacity: 1, x: 0 }}
                      transition={{ delay: 0.3 + idx * 0.1, duration: 0.4 }}
                      viewport={{ once: true }}
                    >
                      <CheckCircle2
                        className={`w-4 h-4 flex-shrink-0 ${
                          service.featured ? "text-primary" : "text-primary/70"
                        }`}
                      />
                      <span className="text-foreground/80">{feature}</span>
                    </motion.li>
                  ))}
                </ul>

                {/* CTA */}
                <motion.div 
                  className="flex items-center text-primary font-medium text-sm group/link cursor-pointer"
                  whileHover={{ x: 6 }}
                  transition={{ duration: 0.2 }}
                >
                  <span>Learn more</span>
                  <ArrowRight className="w-4 h-4 ml-2 transition-transform group-hover/link:translate-x-1" />
                </motion.div>
              </motion.div>
            </motion.div>
          ))}
        </motion.div>

        {/* Bottom CTA with enhanced animation */}
        <motion.div
          initial={{ opacity: 0, y: 30, scale: 0.95 }}
          whileInView={{ opacity: 1, y: 0, scale: 1 }}
          transition={{ duration: 0.8, delay: 0.2 }}
          viewport={{ once: true }}
          className="text-center"
        >
          <motion.div 
            className="inline-flex flex-col sm:flex-row items-center gap-4 bg-secondary/50 rounded-2xl p-6 sm:p-8"
            whileHover={{ scale: 1.02 }}
            transition={{ duration: 0.3 }}
          >
            <div className="text-center sm:text-left">
              <p className="font-display text-xl text-foreground mb-1">
                Ready to begin your journey?
              </p>
              <p className="text-muted-foreground text-sm">
                Schedule a confidential consultation to discuss your investment goals.
              </p>
            </div>
            <Button variant="luxury" size="lg" className="whitespace-nowrap">
              Book Consultation
            </Button>
          </motion.div>
        </motion.div>
      </div>
    </section>
  );
};

export default ServicesSection;
